import { useParams, useNavigate } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { IconArrow, IconMapPin, IconClock, IconWhatsapp } from '../components/Icons';
import styles from './ExperienceDetails.module.css';

export default function ExperienceDetails() {
  const { slug } = useParams();
  const { data } = useSite();
  const navigate = useNavigate();

  const experience = (data.events || []).find(e => e.slug === slug);
  const whatsappPhone = (data.contact?.whatsapp || data.contact?.phone || '+962776113530').replace(/[^0-9]/g, '');

  if (!experience) {
    return (
      <>
        <SEO
          title="Experience Not Found | Keepers"
          description="The experience you're looking for is not available. Explore other unique outdoor adventures."
          noindex={true}
        />
        <div className={styles.page}>
          <Navbar />
          <div className={styles.notFound}>
            <div className={styles.notFoundIcon}>✨</div>
            <h2>Experience not found</h2>
            <button className={styles.backBtn} onClick={() => navigate('/experiences')}>
              <span className={styles.backArrow}><IconArrow size={14} /></span> Back to Experiences
            </button>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const bookOnWhatsapp = () => {
    const msg = encodeURIComponent(`Hi! I'd like to book a spot for: ${experience.title}`);
    window.open(`https://wa.me/${whatsappPhone}?text=${msg}`, '_blank');
  };

  return (
    <>
      <SEO
        title={`${experience.title} | Unique Experience in Jordan - Keepers`}
        description={experience.description || `Join us for ${experience.title} in Jordan`}
        image={experience.image || 'https://keepersexperience.com/full_logo.png'}
        url={`https://keepersexperience.com/experiences/${experience.slug}`}
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          "name": experience.title,
          "description": experience.description,
          "image": experience.image,
          "location": {
            "@type": "Place",
            "name": experience.location || "Jordan"
          }
        }}
      />
      <div className={styles.page}>
      <Navbar />

      {/* ─── BACK BUTTON ─── */}
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <span className={styles.backArrow}><IconArrow size={14} /></span>
          Back
        </button>
      </div>

      {/* ─── HERO IMAGE ─── */}
      <div className={styles.heroSection}>
        {experience.image ? (
          <>
            <img src={experience.image} alt={experience.title} className={styles.heroImg} />
            <div className={styles.heroOverlay} />
          </>
        ) : (
          <div className={styles.heroPlaceholder}>✨</div>
        )}

        {experience.upcoming && (
          <span className={styles.heroBadge}>🗓 Upcoming</span>
        )}

        {/* Title overlay at bottom of hero */}
        <div className={styles.heroBottom}>
          {(experience.tags || []).length > 0 && (
            <div className={styles.heroTags}>
              {experience.tags.map(tag => (
                <span key={tag} className={styles.heroTag}>{tag}</span>
              ))}
            </div>
          )}
          <h1 className={styles.heroTitle}>{experience.title}</h1>
        </div>
      </div>

      {/* ─── CONTENT ─── */}
      <div className={styles.content}>
        <div className={styles.contentInner}>

          {/* Info Grid */}
          <div className={styles.infoGrid}>
            {experience.location && (
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}><IconMapPin size={18} /></span>
                <div>
                  <div className={styles.infoLabel}>Location</div>
                  <div className={styles.infoValue}>{experience.location}</div>
                </div>
              </div>
            )}
            {experience.date && (
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}>📅</span>
                <div>
                  <div className={styles.infoLabel}>Date</div>
                  <div className={styles.infoValue}>{experience.date}</div>
                </div>
              </div>
            )}
            {experience.time && (
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}><IconClock size={18} /></span>
                <div>
                  <div className={styles.infoLabel}>Time</div>
                  <div className={styles.infoValue}>{experience.time}</div>
                </div>
              </div>
            )}
            {experience.fees && (
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}>💰</span>
                <div>
                  <div className={styles.infoLabel}>Fees</div>
                  <div className={styles.infoValue}>{experience.fees}</div>
                </div>
              </div>
            )}
            {experience.groupSize && (
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}>👥</span>
                <div>
                  <div className={styles.infoLabel}>Group Size</div>
                  <div className={styles.infoValue}>{experience.groupSize}</div>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          {experience.description && (
            <div className={styles.descSection}>
              <h2 className={styles.descTitle}>About this experience</h2>
              <p className={styles.desc}>{experience.description}</p>
            </div>
          )}

          {/* CTA */}
          <div className={styles.cta}>
            <button className={styles.whatsappBtn} onClick={bookOnWhatsapp}>
              <IconWhatsapp size={18} /> Book via WhatsApp
            </button>
            <button className={styles.allBtn} onClick={() => navigate('/experiences')}>
              <span className={styles.allBtnArrow}><IconArrow size={14} /></span>
              All Experiences
            </button>
          </div>

        </div>
      </div>

      <Footer />
    </div>
    </>
  );
}
